import { useState } from "react";
import { X, ArrowClockwise, CheckCircle, Info, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function SubscribeModal({ plan, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [applied, setApplied] = useState(false);
  const [checking, setChecking] = useState(false);
  const [loading, setLoading] = useState(false);

  const monthly = plan.monthly_price ?? 0;
  const price = plan.price ?? 0;
  const setup = applied ? Math.round(price * 0.9 * 100) / 100 : price;
  const dueToday = Math.round((setup + monthly) * 100) / 100;

  const validateCode = async () => {
    if (!code.trim()) return;
    setChecking(true);
    try {
      const res = await api.post("/referral/validate", { code });
      if (res.data.valid) { setApplied(true); toast.success("Referral code applied — 10% off setup!"); }
      else { setApplied(false); toast.error(res.data.message || "Invalid code"); }
    } catch { toast.error("Could not validate code"); }
    finally { setChecking(false); }
  };

  const start = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/stripe/create-subscription-checkout", {
        plan: plan.name, name, email, code: applied ? code : null, origin_url: window.location.origin,
      });
      if (res.data.already_active) {
        toast.info("You already have an active subscription. Opening billing portal…");
        const portal = await api.post("/stripe/billing-portal", { email, origin_url: window.location.origin });
        window.location.href = portal.data.url;
        return;
      }
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-white border border-black/15 px-3 py-2.5 outline-none focus:border-black transition-colors text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto border border-black/10" onClick={(e) => e.stopPropagation()} data-testid="subscribe-modal">
        <div className="sticky top-0 bg-white border-b border-black/10 px-6 py-4 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2">
            <ArrowClockwise size={20} /> {plan.name} Plan
          </h2>
          <button onClick={onClose} data-testid="subscribe-close" className="p-1.5 hover:bg-zinc-100 rounded-lg"><X size={20} /></button>
        </div>

        <form onSubmit={start} className="p-6">
          {/* summary */}
          <div className="border border-black/10 divide-y divide-black/10 text-sm">
            <div className="flex justify-between p-3"><span className="text-zinc-500">Website setup (one-time)</span><span>${price.toLocaleString()}</span></div>
            {applied && <div className="flex justify-between p-3 text-zinc-600"><span>Referral discount (10% off setup)</span><span>−${(price * 0.1).toLocaleString()}</span></div>}
            <div className="flex justify-between p-3"><span className="text-zinc-500">First month maintenance</span><span>${monthly.toLocaleString()}</span></div>
            <div className="flex justify-between p-3 font-semibold"><span>Due today</span><span data-testid="subscribe-due-today">${dueToday.toLocaleString()}</span></div>
          </div>

          <div className="mt-4 border border-black/10 bg-zinc-50 p-4 text-sm text-zinc-600 space-y-1.5">
            <div className="flex items-start gap-2"><Info size={16} className="mt-0.5 shrink-0" /> You're charged <b>${dueToday.toLocaleString()} today</b> ($ {setup.toLocaleString()} setup + first month), then <b>${monthly.toLocaleString()}/month automatically</b>.</div>
            <div className="flex items-start gap-2"><CheckCircle size={16} className="mt-0.5 shrink-0" /> Cancel anytime from the billing portal — the setup and subscription go together.</div>
          </div>

          {/* referral */}
          <div className="mt-5">
            <label className="text-sm font-medium">Referral code (optional)</label>
            <div className="mt-1.5 flex gap-2">
              <input data-testid="subscribe-code-input" value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); setApplied(false); }} placeholder="ENTER CODE" className={`${inputCls} uppercase tracking-widest`} />
              <button type="button" data-testid="subscribe-apply-code" onClick={validateCode} disabled={checking} className="bg-black text-white font-medium px-5 hover:bg-zinc-800 transition-colors disabled:opacity-50 text-sm">{checking ? "…" : "Apply"}</button>
            </div>
            {applied && <div className="mt-1.5 text-sm flex items-center gap-1.5 text-black"><Tag size={16} weight="fill" /> 10% off your setup fee</div>}
          </div>

          <div className="mt-5 space-y-4">
            <div>
              <label className="text-sm font-medium">Name</label>
              <input data-testid="subscribe-name" className={`mt-1.5 ${inputCls}`} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium">Email *</label>
              <input required type="email" data-testid="subscribe-email" className={`mt-1.5 ${inputCls}`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.com" />
            </div>
          </div>

          <button type="submit" data-testid="subscribe-start-btn" disabled={loading} className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-black text-white font-medium py-3.5 hover:bg-zinc-800 transition-colors disabled:opacity-50">
            {loading ? "Redirecting…" : `Get Started — $${dueToday.toLocaleString()} today`}
          </button>
          <p className="mt-3 text-xs text-zinc-400 text-center">Secure checkout by Stripe. Apple Pay & cards supported.</p>
        </form>
      </div>
    </div>
  );
}
